import React, { useEffect, useRef, useState } from "react";

/* =========================================================================
   LEDGER — Vintage Academic Study Planner
   Single-component static SPA. Cream parchment + navy + gold + crimson.
   ========================================================================= */

const STYLES = `
:root {
  --cream:        #F5EAD0;
  --cream-2:      #EFE2BF;
  --cream-light:  #FBF4DC;
  --paper:        #FAF1D6;
  --navy:         #0E2240;
  --navy-2:       #16314E;
  --navy-line:    #243C5C;
  --ink:          #1B2A45;
  --ink-soft:     #2E3E5C;
  --gold:         #B58A36;
  --gold-soft:    #C9A24A;
  --gold-line:    #D9BE72;
  --crimson:      #7A1E1E;
  --crimson-hov:  #8E2828;
  --muted:        #8C7C5A;
  --hairline:     #DCC58A;
  --shadow-card:  0 1px 0 rgba(120,90,30,0.08), 0 12px 32px -18px rgba(40,25,0,0.18);
  --font-display: 'Cormorant Garamond', 'Playfair Display', Georgia, serif;
  --font-serif:   'Lora', 'EB Garamond', Georgia, serif;
  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 14px;
}

* { box-sizing: border-box; margin: 0; padding: 0; }
html, body, #root { height: 100%; }
body {
  font-family: var(--font-serif);
  background: var(--cream);
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
  background-image:
    radial-gradient(rgba(150,110,40,0.06) 1px, transparent 1px),
    radial-gradient(rgba(150,110,40,0.04) 1px, transparent 1px);
  background-size: 3px 3px, 7px 7px;
  background-position: 0 0, 1px 2px;
}

/* ---------- App shell ---------- */
.ledger-app { display: flex; height: 100vh; overflow: hidden; }

/* Default styling for inline icon SVGs (line icons inherit color, no fill).
   Illustrations (Engraving / LampBook / Quill) always set their own fill+stroke attrs,
   so this rule only targets the icon library svgs. */
.ledger-app svg:not([fill]):not([stroke]) {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
.continue-card .note-pill .doc-icon svg { width: 22px; height: 22px; }
.btn svg { width: 16px; height: 16px; }
.tb-btn svg { width: 16px; height: 16px; }
.editor-meta svg { width: 14px; height: 14px; color: var(--gold); }
.qa-row svg, .coll-row svg, .folder-item svg, .set-row svg { width: 16px; height: 16px; }
.amb-btn svg { width: 22px; height: 22px; }
.icon-btn svg, .avatar-pill svg, .search-pill svg, .nav-item svg, .tt-tab svg { /* sized by their own rules */ }
.doc-row .doc-icon-box svg { width: 22px; height: 26px; }
.pin svg { width: 14px; height: 14px; color: var(--gold); }

/* ---------- Sidebar ---------- */
.sidebar {
  width: 240px;
  flex-shrink: 0;
  background: var(--navy);
  color: #E6D9B6;
  display: flex;
  flex-direction: column;
  padding: 28px 0 20px 0;
  border-right: 1px solid #0a1a30;
  position: relative;
}
.brand {
  text-align: center;
  padding: 0 24px 18px;
}
.brand-name {
  font-family: var(--font-display);
  font-weight: 500;
  letter-spacing: 0.32em;
  color: var(--gold-line);
  font-size: 26px;
}
.ornament {
  display: flex; align-items: center; justify-content: center;
  margin: 8px 0;
  color: var(--gold);
}
.ornament svg { display: block; }
.brand .ornament { margin-top: 10px; }

.nav-list { list-style: none; padding: 24px 14px; display: flex; flex-direction: column; gap: 4px; flex-grow: 1; }
.nav-item {
  display: flex; align-items: center; gap: 14px;
  padding: 12px 16px;
  border-radius: 6px;
  color: #C8BC9A;
  font-family: var(--font-serif);
  font-size: 16px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  width: 100%;
  transition: background 160ms ease, color 160ms ease;
}
.nav-item:hover { color: #F2E6BF; background: rgba(255,255,255,0.04); }
.nav-item.active {
  background: linear-gradient(90deg, rgba(217,190,114,0.18), rgba(217,190,114,0.05));
  color: #F5E6B5;
  box-shadow: inset 3px 0 0 var(--gold);
}
.nav-item svg { width: 18px; height: 18px; stroke: currentColor; fill: none; stroke-width: 1.6; flex-shrink: 0; }

.sidebar-quote {
  padding: 18px 26px 6px;
  text-align: center;
  font-style: italic;
  color: #B6A77E;
  font-size: 13px;
  line-height: 1.55;
}
.sidebar-quote .attrib { display: block; margin-top: 8px; font-style: normal; color: #978868; font-size: 12px; }

/* ---------- Main area ---------- */
.app-main { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; }

.topbar {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 28px 40px 8px;
  gap: 24px;
}
.greeting-block { flex-grow: 1; }
.greeting-hello {
  font-family: var(--font-serif);
  font-size: 18px;
  color: var(--ink-soft);
  font-style: italic;
}
.greeting-name {
  font-family: var(--font-display);
  font-size: 56px;
  font-weight: 500;
  color: var(--ink);
  line-height: 1.05;
  letter-spacing: -0.01em;
}
.page-title {
  font-family: var(--font-display);
  font-size: 44px;
  font-weight: 500;
  color: var(--ink);
  display: flex; align-items: center; gap: 14px;
}
.page-title .orn { color: var(--gold); }

.search-pill {
  display: flex; align-items: center; gap: 10px;
  background: var(--paper);
  border: 1px solid var(--hairline);
  border-radius: 999px;
  padding: 10px 18px;
  width: 360px;
  height: 46px;
}
.search-pill input {
  flex-grow: 1; border: none; background: transparent; outline: none;
  font-family: var(--font-serif); font-size: 14px; color: var(--ink);
}
.search-pill input::placeholder { color: #9C8A5E; font-style: italic; }
.search-pill svg { width: 16px; height: 16px; stroke: var(--muted); fill: none; stroke-width: 2; }

.topbar-actions { display: flex; align-items: center; gap: 14px; }
.icon-btn {
  width: 44px; height: 44px; border-radius: 50%;
  border: 1px solid var(--hairline);
  background: var(--paper);
  color: var(--ink-soft);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.icon-btn svg { width: 18px; height: 18px; stroke: currentColor; fill: none; stroke-width: 1.8; }
.avatar-pill {
  display: flex; align-items: center; gap: 8px;
  padding: 4px 10px 4px 4px;
  border-radius: 999px;
  border: 1px solid var(--hairline);
  background: var(--paper);
  cursor: pointer;
}
.avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: var(--crimson); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-family: var(--font-display); font-weight: 600; font-size: 18px;
}
.avatar-pill svg { width: 14px; height: 14px; stroke: var(--muted); fill: none; stroke-width: 2; }

/* ---------- Scrollway / content ---------- */
.scrollway { flex-grow: 1; overflow-y: auto; padding: 8px 40px 40px; }
.view { animation: fade 260ms ease both; }
@keyframes fade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

/* ---------- Card (parchment frame) ---------- */
.card {
  background: var(--paper);
  border-radius: var(--radius-lg);
  border: 1px solid var(--hairline);
  box-shadow: var(--shadow-card);
  position: relative;
  padding: 28px;
}
.card.with-corners::before, .card.with-corners::after,
.card.with-corners > .corner-tl, .card.with-corners > .corner-tr,
.card.with-corners > .corner-bl, .card.with-corners > .corner-br {
  content: '';
  position: absolute;
  width: 14px; height: 14px;
  border: 1px solid var(--gold-line);
}
.card.with-corners > .corner-tl { top: 8px; left: 8px; border-right: none; border-bottom: none; }
.card.with-corners > .corner-tr { top: 8px; right: 8px; border-left: none; border-bottom: none; }
.card.with-corners > .corner-bl { bottom: 8px; left: 8px; border-right: none; border-top: none; }
.card.with-corners > .corner-br { bottom: 8px; right: 8px; border-left: none; border-top: none; }

.section-title {
  font-family: var(--font-serif);
  font-size: 12px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--gold);
  font-weight: 600;
}
.section-rule { height: 1px; background: var(--gold-line); margin: 10px 0 18px; opacity: 0.6; position: relative; }
.section-rule::before {
  content: ''; position: absolute; left: 0; top: -2px;
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--gold);
}

/* ---------- Home Dashboard ---------- */
.home-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-top: 8px;
}
.continue-card { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: center; min-height: 360px; }
.engraving {
  width: 100%; height: 280px;
  border-radius: 10px;
  background:
    radial-gradient(ellipse at center bottom, rgba(120,90,30,0.18), transparent 60%),
    linear-gradient(180deg, #F8EFD2, #EFE0B5);
  display: flex; align-items: center; justify-content: center;
  color: #8a6a2a;
}
.engraving svg { width: 92%; height: 92%; opacity: 0.85; }
.continue-right { padding: 8px 12px; }
.continue-title { font-family: var(--font-display); font-size: 32px; font-weight: 500; color: var(--ink); margin-bottom: 6px; }
.continue-sub { color: var(--ink-soft); font-size: 14px; margin-bottom: 16px; font-style: italic; }
.note-pill {
  display: flex; align-items: center; gap: 12px;
  border: 1px solid var(--hairline);
  background: var(--cream-light);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 18px;
}
.note-pill .doc-icon { width: 22px; height: 22px; color: var(--ink-soft); }
.note-pill-title { font-family: var(--font-display); font-size: 18px; font-weight: 600; color: var(--ink); }
.note-pill-meta { font-size: 12px; color: var(--muted); }

.btn {
  font-family: var(--font-serif);
  font-size: 15px;
  padding: 12px 22px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 10px;
  transition: background 160ms ease, transform 120ms ease;
}
.btn:active { transform: scale(0.98); }
.btn-primary { background: var(--crimson); color: #FBF1D6; }
.btn-primary:hover { background: var(--crimson-hov); }
.btn-ghost { background: transparent; color: var(--ink); border: 1px solid var(--hairline); }
.btn-ghost:hover { background: var(--cream-light); }
.btn-arrow svg { width: 16px; height: 16px; stroke: currentColor; fill: none; stroke-width: 2; }

.tasks-list { display: flex; flex-direction: column; gap: 14px; margin-top: 8px; }
.task-row { display: flex; align-items: center; gap: 14px; }
.checkbox {
  width: 18px; height: 18px; border: 1.5px solid var(--gold); border-radius: 4px;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  background: transparent;
  flex-shrink: 0;
}
.checkbox.checked { background: var(--gold); }
.checkbox.checked::after { content: ''; width: 10px; height: 5px; border-left: 2px solid #fff; border-bottom: 2px solid #fff; transform: rotate(-45deg) translate(0, -1px); }
.task-name { font-family: var(--font-display); font-size: 18px; font-weight: 600; color: var(--ink); }
.task-sub { color: var(--ink-soft); font-size: 14px; flex-grow: 1; }
.task-time { color: var(--ink); font-size: 14px; }
.link-action { color: #1f4f96; cursor: pointer; font-size: 14px; }
.link-action:hover { color: #163b73; text-decoration: underline; }

.lower-grid { display: grid; grid-template-columns: 1fr 1fr 1.4fr; gap: 24px; margin-top: 24px; }
.doc-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; }
.doc-row + .doc-row { border-top: 1px dashed rgba(181,138,54,0.25); }
.doc-icon-box {
  width: 28px; height: 32px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  color: var(--ink-soft);
}
.doc-icon-box.pdf { color: var(--crimson); }
.doc-icon-box.img { color: #6b6b3a; }
.doc-icon-box.link { color: #1f4f96; }
.doc-meta { display: flex; flex-direction: column; flex-grow: 1; }
.doc-title { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ink); }
.doc-sub { font-size: 12px; color: var(--muted); }
.pin {
  color: var(--ink-soft); opacity: 0.7;
}

/* Mini timetable */
.mini-tt { width: 100%; border-collapse: collapse; font-size: 13px; }
.mini-tt th { color: var(--muted); font-weight: 600; padding: 8px 6px; font-size: 11px; letter-spacing: 0.1em; }
.mini-tt td {
  padding: 8px 8px;
  vertical-align: middle;
  border-top: 1px solid rgba(181,138,54,0.18);
  color: var(--ink);
}
.mini-tt td.time-col { font-size: 11px; color: var(--muted); width: 64px; line-height: 1.3; }
.mini-tt td .subject { display: block; padding-left: 8px; border-left: 3px solid; line-height: 1.2; }
.s-math { border-color: #2E5A91; }
.s-eng  { border-color: #B58A36; }
.s-phy  { border-color: #6A4F8F; }
.s-chem { border-color: #3D7A4D; }
.s-bio  { border-color: #4F8F66; }
.s-cs   { border-color: #2E5A91; }
.s-hist { border-color: #7A1E1E; }
.s-sports { border-color: #7A1E1E; }
.s-tut  { border-color: #8c7748; }

/* Focus banner */
.focus-banner {
  margin-top: 24px;
  display: grid; grid-template-columns: auto 1fr auto; gap: 24px; align-items: center;
  padding: 22px 28px;
}
.lamp-svg { width: 70px; height: 70px; color: #8a6a2a; }
.fb-title { font-family: var(--font-display); font-size: 26px; color: var(--ink); margin-bottom: 4px; }
.fb-sub { color: var(--ink-soft); font-size: 14px; }

/* ---------- Notes view ---------- */
.notes-layout { display: grid; grid-template-columns: 280px 1fr; gap: 24px; margin-top: 8px; height: calc(100vh - 200px); }
.folder-panel { padding: 18px; display: flex; flex-direction: column; gap: 8px; overflow-y: auto; }
.folder-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border-radius: 8px;
  font-size: 15px; color: var(--ink); cursor: pointer;
  border: none; background: transparent; width: 100%; text-align: left;
  font-family: var(--font-serif);
}
.folder-item:hover { background: var(--cream-light); }
.folder-item.active { background: var(--cream-2); }
.folder-item .count { margin-left: auto; color: var(--muted); font-size: 13px; }
.folder-item svg { width: 16px; height: 16px; stroke: var(--gold); fill: none; stroke-width: 1.6; }
.folder-section-title { font-size: 11px; color: var(--gold); letter-spacing: 0.2em; padding: 12px 8px 4px; display: flex; justify-content: space-between; }
.add-folder { color: var(--crimson); cursor: pointer; padding: 8px 12px; font-size: 14px; }

.editor-panel { padding: 28px 36px; overflow-y: auto; display: flex; flex-direction: column; }
.editor-head { display: flex; align-items: center; gap: 12px; margin-bottom: 6px; }
.editor-title-input {
  font-family: var(--font-display); font-size: 36px; font-weight: 600;
  border: none; background: transparent; outline: none; color: var(--ink);
  width: 100%;
}
.editor-meta { display: flex; align-items: center; gap: 12px; font-size: 13px; color: var(--muted); margin-bottom: 18px; }
.editor-toolbar {
  display: flex; gap: 4px; padding: 8px;
  border: 1px solid var(--hairline);
  border-radius: 8px;
  background: var(--cream-light);
  margin-bottom: 22px;
  flex-wrap: wrap;
}
.tb-btn {
  width: 32px; height: 32px; border-radius: 6px;
  border: none; background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--ink-soft); font-family: var(--font-display); font-weight: 600;
}
.tb-btn:hover { background: var(--cream-2); }
.tb-divider { width: 1px; background: var(--hairline); margin: 4px 6px; }

.note-body { font-size: 16px; line-height: 1.75; color: var(--ink); }
.note-body h3 { font-family: var(--font-display); font-size: 26px; font-weight: 600; margin: 18px 0 12px; color: var(--ink); }
.note-body ul { padding-left: 22px; margin: 8px 0 14px; }
.note-body li { margin-bottom: 8px; }
.note-body strong { color: var(--ink); }
.note-body .fleur-rule { display: flex; align-items: center; gap: 12px; margin: 24px 0; color: var(--gold); }
.note-body .fleur-rule::before, .note-body .fleur-rule::after { content: ''; flex-grow: 1; height: 1px; background: var(--gold-line); }
.note-body table.timeline { width: 100%; border-collapse: collapse; margin: 12px 0 20px; border: 1px solid var(--hairline); border-radius: 8px; overflow: hidden; }
.note-body table.timeline th, .note-body table.timeline td { text-align: left; padding: 12px 16px; border-bottom: 1px solid var(--hairline); font-size: 15px; }
.note-body table.timeline th { background: var(--cream-2); font-family: var(--font-display); font-weight: 600; font-size: 16px; }
.note-body .callout {
  background: var(--cream-2);
  border: 1px solid var(--hairline);
  border-radius: 8px;
  padding: 16px 20px;
  display: flex; gap: 14px; margin: 16px 0;
}
.note-body .callout svg { width: 28px; height: 28px; color: var(--gold); flex-shrink: 0; }

/* ---------- Timetable view ---------- */
.tt-bar { display: flex; align-items: center; gap: 24px; padding: 8px 0 20px; }
.tt-tabs { display: flex; gap: 24px; }
.tt-tab {
  background: transparent; border: none; cursor: pointer;
  font-family: var(--font-serif); font-size: 16px;
  color: var(--ink-soft); padding: 8px 4px; display: flex; gap: 8px; align-items: center;
}
.tt-tab.active { color: var(--ink); border-bottom: 2px solid var(--ink); font-weight: 600; }
.tt-tab svg { width: 18px; height: 18px; stroke: currentColor; fill: none; stroke-width: 1.6; }
.tt-actions { margin-left: auto; display: flex; gap: 12px; }

.tt-grid { display: grid; grid-template-columns: 1fr 280px; gap: 24px; }
.tt-table-wrap { overflow-x: auto; }
.tt-table { width: 100%; border-collapse: collapse; }
.tt-table th, .tt-table td {
  border: 1px solid var(--hairline);
  padding: 0;
  vertical-align: top;
  background: var(--paper);
}
.tt-table thead th {
  font-family: var(--font-display); font-weight: 600; font-size: 14px;
  color: var(--muted); letter-spacing: 0.15em; text-align: center; padding: 14px 8px;
  background: transparent; border: none;
}
.tt-table .time-cell { width: 70px; padding: 14px 8px; font-size: 12px; color: var(--muted); text-align: center; line-height: 1.4; background: transparent; border: none; }
.tt-cell { padding: 4px; }
.tt-block {
  border-radius: 6px;
  padding: 10px 12px;
  height: 70px;
  font-size: 13px;
  display: flex; flex-direction: column; justify-content: center;
  position: relative;
  border-left: 4px solid transparent;
}
.tt-block .name { font-family: var(--font-display); font-weight: 600; font-size: 15px; }
.tt-block .room { font-size: 12px; color: var(--ink-soft); }
.tt-block.empty { color: var(--muted); text-align: center; padding-top: 26px; font-size: 18px; }

.subject-card { display: flex; flex-direction: column; gap: 10px; }
.subj-row { display: flex; align-items: center; gap: 10px; padding: 6px 0; }
.subj-dot { width: 10px; height: 10px; border-radius: 50%; }
.subj-name { flex-grow: 1; font-family: var(--font-display); font-size: 16px; }
.subj-count { color: var(--muted); font-size: 13px; }
.subj-total { display: flex; justify-content: space-between; padding-top: 10px; border-top: 1px solid var(--hairline); margin-top: 6px; font-family: var(--font-display); font-size: 16px; }

.qa-row { display: flex; align-items: center; gap: 12px; padding: 8px 0; color: var(--ink); cursor: pointer; }
.qa-row svg { width: 18px; height: 18px; stroke: var(--ink-soft); fill: none; stroke-width: 1.6; }
.qa-row:hover { color: var(--crimson); }

/* ---------- Checklist view ---------- */
.checklist-bar { display: flex; align-items: center; gap: 28px; padding: 8px 0 20px; }
.cl-grid { display: grid; grid-template-columns: 1.6fr 1fr; gap: 24px; }
.cl-task {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(181,138,54,0.15);
}
.cl-task:last-child { border-bottom: none; }
.cl-task .grow { flex-grow: 1; }
.cl-task .star { color: var(--gold); }
.cl-task.done .task-name { text-decoration: line-through; color: var(--muted); }

.donut-wrap { display: grid; grid-template-columns: auto 1fr; gap: 24px; align-items: center; }
.donut { width: 140px; height: 140px; }
.donut .ring-bg { stroke: var(--cream-2); }
.donut .ring-fg { stroke: #5a8a5a; transition: stroke-dashoffset 400ms; }
.donut-text { text-anchor: middle; font-family: var(--font-display); font-size: 22px; fill: var(--ink); }
.donut-sub { text-anchor: middle; font-size: 10px; fill: var(--muted); letter-spacing: 0.15em; }
.kpi-row { display: flex; gap: 20px; }
.kpi { text-align: center; }
.kpi .num { font-family: var(--font-display); font-size: 30px; font-weight: 600; color: var(--ink); }
.kpi .lbl { font-size: 11px; letter-spacing: 0.15em; color: var(--muted); text-transform: uppercase; }
.divider-soft { height: 1px; background: var(--hairline); margin: 18px 0; opacity: 0.6; }

/* ---------- Library view ---------- */
.lib-tabs { display: flex; gap: 28px; padding: 8px 0 16px; }
.lib-grid { display: grid; grid-template-columns: 280px 1fr; gap: 24px; }
.coll-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 6px; cursor: pointer; }
.coll-row:hover { background: var(--cream-light); }
.coll-row.active { background: var(--cream-2); }
.coll-row svg { width: 16px; height: 16px; stroke: var(--gold); fill: none; stroke-width: 1.6; }
.coll-row .count { margin-left: auto; color: var(--muted); font-size: 13px; }
.storage-bar { height: 6px; background: var(--cream-2); border-radius: 4px; overflow: hidden; margin: 12px 0; }
.storage-fill { height: 100%; background: linear-gradient(90deg, #B58A36, #C9A24A); width: 23%; }

.book-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.book-card { display: flex; flex-direction: column; align-items: center; padding: 18px; }
.book-thumb { width: 90px; height: 110px; margin-bottom: 12px; display: flex; align-items: center; justify-content: center; }
.book-thumb.pdf { color: var(--crimson); }
.book-name { font-family: var(--font-display); font-size: 16px; text-align: center; color: var(--ink); margin-bottom: 4px; }
.book-meta { font-size: 12px; color: var(--muted); text-align: center; }
.book-date { font-size: 11px; color: var(--muted); margin-top: 12px; }

/* ---------- Focus view ---------- */
.focus-grid { display: grid; grid-template-columns: 1.6fr 1fr; gap: 24px; }
.focus-timer-display {
  font-family: var(--font-display);
  font-size: 96px;
  font-weight: 500;
  text-align: center;
  color: var(--ink);
  letter-spacing: -0.02em;
  line-height: 1;
  margin: 16px 0;
}
.timer-label { text-align: center; font-size: 12px; letter-spacing: 0.2em; color: var(--muted); text-transform: uppercase; }
.ambience-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.amb-btn {
  background: var(--cream-light); border: 1px solid var(--hairline);
  border-radius: 8px; padding: 14px 6px;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  cursor: pointer; font-family: var(--font-serif); font-size: 13px; color: var(--ink);
}
.amb-btn.active { background: var(--cream-2); border-color: var(--gold); box-shadow: 0 0 0 2px rgba(181,138,54,0.15); }
.amb-btn svg { width: 22px; height: 22px; color: var(--ink-soft); }
.sound-row { display: flex; align-items: center; gap: 12px; margin-top: 12px; }
.sound-row input[type=range] { flex-grow: 1; accent-color: var(--ink); }
.toggle-row { display: flex; align-items: center; gap: 12px; padding-top: 8px; }
.toggle-row .checkbox { border-color: var(--ink-soft); }

.focus-stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 24px; }
.progress-bar { height: 10px; background: var(--cream-2); border-radius: 6px; overflow: hidden; margin: 8px 0; }
.progress-fill { height: 100%; background: var(--crimson); width: 67%; }

/* ---------- Settings ---------- */
.set-grid { display: grid; grid-template-columns: 240px 1fr 280px; gap: 24px; }
.set-section-title { font-size: 11px; letter-spacing: 0.2em; color: var(--gold); padding: 14px 12px 6px; text-transform: uppercase; }
.set-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 6px; cursor: pointer; font-size: 15px; }
.set-row:hover { background: var(--cream-light); }
.set-row.active { background: var(--cream-2); }
.set-row svg { width: 16px; height: 16px; stroke: var(--ink-soft); fill: none; stroke-width: 1.6; }
.field-row { display: flex; align-items: center; justify-content: space-between; padding: 16px 0; border-bottom: 1px solid rgba(181,138,54,0.15); }
.field-row:last-child { border-bottom: none; }
.field-label { font-family: var(--font-display); font-size: 17px; }
.field-sub { font-size: 13px; color: var(--muted); }
.select-pill { padding: 8px 14px; border: 1px solid var(--hairline); border-radius: 8px; background: var(--cream-light); font-family: var(--font-serif); }
.color-dots { display: flex; gap: 8px; }
.color-dot { width: 22px; height: 22px; border-radius: 50%; cursor: pointer; border: 2px solid transparent; }
.color-dot.active { border-color: var(--ink); }
.switch { width: 44px; height: 24px; border-radius: 999px; background: var(--cream-2); border: 1px solid var(--hairline); position: relative; cursor: pointer; }
.switch::after { content: ''; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: left 200ms; }
.switch.on { background: var(--navy); }
.switch.on::after { left: 22px; }

/* ---------- Toast ---------- */
.toast {
  position: fixed; bottom: 24px; right: 24px;
  background: var(--paper); border: 1px solid var(--hairline); border-left: 4px solid var(--gold);
  padding: 12px 18px; border-radius: 8px;
  font-family: var(--font-serif); color: var(--ink);
  box-shadow: 0 10px 25px -8px rgba(0,0,0,0.15);
  transform: translateY(80px); opacity: 0;
  transition: all 240ms ease;
  z-index: 1000;
}
.toast.show { transform: translateY(0); opacity: 1; }
.empty { text-align: center; color: var(--muted); padding: 32px; font-style: italic; }
.muted { color: var(--muted); }

/* tiny utilities */
.row { display: flex; align-items: center; }
.gap-8 { gap: 8px; } .gap-12 { gap: 12px; }
.between { justify-content: space-between; }
.mt-16 { margin-top: 16px; } .mt-24 { margin-top: 24px; }
`;

/* ====================== Inline SVG icons (line, gold-tone friendly) ====================== */
const I = {
  home: <svg viewBox="0 0 24 24"><path d="M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" strokeLinejoin="round"/></svg>,
  notes: <svg viewBox="0 0 24 24"><rect x="5" y="3" width="14" height="18" rx="1.5"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>,
  calendar: <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="1.5"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>,
  check: <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 12l3 3 5-6"/></svg>,
  book: <svg viewBox="0 0 24 24"><path d="M4 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H4z"/><path d="M20 4h-7a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h8z"/></svg>,
  focus: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/></svg>,
  settings: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>,
  search: <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3" strokeLinecap="round"/></svg>,
  bell: <svg viewBox="0 0 24 24"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></svg>,
  caret: <svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  arrow: <svg viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  pin: <svg viewBox="0 0 24 24"><path d="M9 4h6l-1 5 3 3v2h-5v5l-1 1-1-1v-5H5v-2l3-3z"/></svg>,
  pdf: <svg viewBox="0 0 24 24"><path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" fill="currentColor" opacity="0.18"/><path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM14 3v5h5"/></svg>,
  image: <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="11" r="1.8"/><path d="M3 17l5-5 4 4 3-3 6 6"/></svg>,
  link: <svg viewBox="0 0 24 24"><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 1 0-5.66-5.66l-1.5 1.5"/><path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 1 0 5.66 5.66l1.5-1.5"/></svg>,
  doc: <svg viewBox="0 0 24 24"><path d="M7 3h8l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM15 3v4h4"/></svg>,
  plus: <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" strokeLinecap="round"/></svg>,
  folder: <svg viewBox="0 0 24 24"><path d="M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>,
  chev_l: <svg viewBox="0 0 24 24"><path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  chev_r: <svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  trees: <svg viewBox="0 0 24 24"><path d="M12 3l5 7h-3v4h-4v-4H7z"/><path d="M12 14v6"/></svg>,
  rain: <svg viewBox="0 0 24 24"><path d="M7 14a4 4 0 1 1 1-7.9A5 5 0 0 1 18 8a3 3 0 0 1 0 6H7z"/><path d="M9 18l-1 2M13 18l-1 2M17 18l-1 2"/></svg>,
  cafe: <svg viewBox="0 0 24 24"><path d="M3 9h13v6a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z"/><path d="M16 11h2a2 2 0 0 1 0 4h-2"/><path d="M7 3v3M11 3v3"/></svg>,
  woods: <svg viewBox="0 0 24 24"><path d="M8 3l4 6H10v4H6V9H4zM16 6l4 6h-2v4h-4v-4h-2z"/></svg>,
  lamp: <svg viewBox="0 0 24 24"><path d="M7 4l5 6"/><path d="M3 14l9-4 2 5-9 3z"/><path d="M14 15v6"/><path d="M10 21h8"/></svg>,
};

/* Ornament: small diamond + lines */
const Ornament = ({ size = 14, color = "currentColor" }) => (
  <svg width={size * 5} height={size} viewBox="0 0 70 14" fill="none">
    <line x1="0" y1="7" x2="28" y2="7" stroke={color} strokeWidth="1" />
    <path d="M35 2 L40 7 L35 12 L30 7 Z" fill={color} />
    <line x1="42" y1="7" x2="70" y2="7" stroke={color} strokeWidth="1" />
  </svg>
);

/* Page title decoration */
const FleurRule = () => (
  <svg width="80" height="14" viewBox="0 0 80 14" fill="none" style={{ display: "block" }}>
    <line x1="0" y1="7" x2="30" y2="7" stroke="currentColor" strokeWidth="1"/>
    <path d="M40 1 c2 3 -2 5 0 6 c-2 1 2 3 0 6 c-2 -3 2 -5 0 -6 c2 -1 -2 -3 0 -6z" fill="currentColor"/>
    <line x1="50" y1="7" x2="80" y2="7" stroke="currentColor" strokeWidth="1"/>
  </svg>
);

/* Engraving illustration: ornate building */
const Engraving = () => (
  <svg viewBox="0 0 320 220" fill="none" stroke="#7a5a20" strokeWidth="1">
    <rect x="40" y="80" width="240" height="110" />
    <path d="M40 80 L160 30 L280 80" />
    <path d="M160 30 L160 18 M155 18h10" />
    <rect x="70" y="100" width="36" height="60" />
    <rect x="120" y="100" width="36" height="60" />
    <rect x="170" y="100" width="36" height="60" />
    <rect x="220" y="100" width="36" height="60" />
    <path d="M40 190 L280 190 L300 200 L20 200 z" fill="#7a5a20" fillOpacity="0.06"/>
    <path d="M55 75 L60 75 M260 75 L265 75" />
    <path d="M40 80 L40 70 L280 70 L280 80" />
    {/* Trees */}
    <path d="M15 200 q -2 -30 8 -50 q 12 22 8 50z" fill="#7a5a20" fillOpacity="0.15"/>
    <path d="M295 200 q 4 -28 12 -48 q 8 24 4 48z" fill="#7a5a20" fillOpacity="0.15"/>
    {/* sky lines */}
    <path d="M0 40 c40 -4 80 4 120 -2" opacity="0.35"/>
    <path d="M200 36 c40 -4 80 4 120 -2" opacity="0.35"/>
  </svg>
);

/* Lamp + book illustration for focus banner */
const LampBookSVG = () => (
  <svg className="lamp-svg" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.2">
    <path d="M30 80 L30 65 q 0 -10 10 -10 L45 55 L45 30 q 0 -8 8 -8 L65 22" />
    <path d="M58 12 L75 22 L70 32 L52 22 z" fill="currentColor" fillOpacity="0.18"/>
    <ellipse cx="40" cy="85" rx="22" ry="3" fill="currentColor" fillOpacity="0.12"/>
    <rect x="20" y="78" width="40" height="6"/>
    <path d="M65 86 q 5 -6 12 -6 q 7 0 12 6" />
    <rect x="68" y="80" width="18" height="6"/>
  </svg>
);

const Quill = () => (
  <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4">
    <path d="M22 4 q -8 4 -14 14 q -2 4 -2 8 l 8 -2 q 6 -4 10 -10 q 2 -4 0 -10z"/>
    <path d="M6 26 l4 -4"/>
    <rect x="2" y="26" width="14" height="3" rx="1"/>
  </svg>
);

/* ============================= DATA ============================= */
const NAV = [
  { id: "home", label: "Home", icon: I.home },
  { id: "notes", label: "Notes", icon: I.notes },
  { id: "timetable", label: "Timetable", icon: I.calendar },
  { id: "checklist", label: "Checklist", icon: I.check },
  { id: "library", label: "Library", icon: I.book },
  { id: "focus", label: "Focus Mode", icon: I.focus },
  { id: "settings", label: "Settings", icon: I.settings },
];

const RECENT_NOTES = [
  { id: 1, title: "French Revolution Notes", sub: "History • 12 pages", pinned: true },
  { id: 2, title: "Algebra Formulae", sub: "Maths • 8 pages", pinned: true },
  { id: 3, title: "Photosynthesis", sub: "Biology • 6 pages", pinned: false },
  { id: 4, title: "Hamlet — Key Themes", sub: "English • 4 pages", pinned: false },
];

const LIBRARY_RECENT = [
  { id: 1, title: "Class 12 Physics Textbook.pdf", sub: "PDF", type: "pdf" },
  { id: 2, title: "Periodic Table of Elements", sub: "Image", type: "image" },
  { id: 3, title: "Organic Chemistry Notes", sub: "PDF", type: "pdf" },
  { id: 4, title: "Useful Websites for Research", sub: "Link", type: "link" },
];

const TASKS_HOME = [
  { id: 1, name: "Maths", sub: "Worksheet 12", time: "5:00 PM", done: false },
  { id: 2, name: "Science", sub: "Lab Report", time: "7:00 PM", done: false },
  { id: 3, name: "English", sub: "Essay Draft", time: "9:00 PM", done: false },
];

const TT_DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT"];
const TT_PREVIEW = [
  { t: "8:00 – 9:00",   row: ["Maths", "English", "Maths", "Physics", "Chemistry", "—"] },
  { t: "9:00 – 10:00",  row: ["Physics", "Maths", "Chemistry", "Maths", "English", "—"] },
  { t: "10:15 – 11:15", row: ["Chemistry", "Physics", "English", "Chemistry", "Maths", "—"] },
  { t: "11:15 – 12:15", row: ["English", "History", "Physics", "English", "Computer\nScience", "—"] },
  { t: "12:45 – 1:45",  row: ["Computer\nScience", "Chemistry", "Maths", "History", "Physics", "—"] },
  { t: "1:45 – 2:45",   row: ["Sports", "Library", "Tutorial", "Sports", "Club", "—"] },
];
const SUBJECT_COLORS = {
  "Maths": "s-math", "English": "s-eng", "Physics": "s-phy", "Chemistry": "s-chem",
  "Biology": "s-bio", "Computer\nScience": "s-cs", "History": "s-hist",
  "Sports": "s-sports", "Library": "s-tut", "Tutorial": "s-tut", "Club": "s-tut", "—": ""
};

/* Full-week table cells */
const FULL_TT = [
  { t: "8:00 – 9:00", row: [
    { s: "Mathematics", r: "Room 201", c: "#2E5A91" },
    { s: "English", r: "Room 105", c: "#B58A36" },
    { s: "Mathematics", r: "Room 201", c: "#2E5A91" },
    { s: "Physics", r: "Lab 3", c: "#6A4F8F" },
    { s: "Chemistry", r: "Lab 2", c: "#3D7A4D" }, null, null
  ]},
  { t: "9:00 – 10:00", row: [
    { s: "History", r: "Room 110", c: "#7A1E1E" },
    { s: "Physics", r: "Lab 3", c: "#6A4F8F" },
    { s: "Chemistry", r: "Lab 2", c: "#3D7A4D" },
    { s: "Mathematics", r: "Room 201", c: "#2E5A91" },
    { s: "English", r: "Room 105", c: "#B58A36" }, null, null
  ]},
  { t: "10:00 – 11:00", row: [
    { s: "English", r: "Room 105", c: "#B58A36" },
    { s: "Mathematics", r: "Room 201", c: "#2E5A91" },
    { s: "History", r: "Room 110", c: "#7A1E1E" },
    { s: "Chemistry", r: "Lab 2", c: "#3D7A4D" },
    { s: "Physics", r: "Lab 3", c: "#6A4F8F" }, null, null
  ]},
  { t: "11:00 – 12:00", row: [
    { s: "Physics", r: "Lab 3", c: "#6A4F8F" },
    { s: "Chemistry", r: "Lab 2", c: "#3D7A4D" },
    { s: "English", r: "Room 105", c: "#B58A36" },
    { s: "History", r: "Room 110", c: "#7A1E1E" },
    { s: "Mathematics", r: "Room 201", c: "#2E5A91" }, null, null
  ]},
  { t: "12:00 – 1:00", row: ["Lunch Break","Lunch Break","Lunch Break","Lunch Break","Lunch Break", null, null], lunch: true },
  { t: "1:00 – 2:00", row: [
    { s: "Computer Science", r: "Lab 1", c: "#2E5A91" },
    { s: "Biology", r: "Lab 4", c: "#4F8F66" },
    { s: "Computer Science", r: "Lab 1", c: "#2E5A91" },
    { s: "English", r: "Room 105", c: "#B58A36" },
    { s: "History", r: "Room 110", c: "#7A1E1E" }, null, null
  ]},
  { t: "2:00 – 3:00", row: [
    { s: "Chemistry", r: "Lab 2", c: "#3D7A4D" },
    { s: "History", r: "Room 110", c: "#7A1E1E" },
    { s: "Physics", r: "Lab 3", c: "#6A4F8F" },
    { s: "Computer Science", r: "Lab 1", c: "#2E5A91" },
    { s: "Biology", r: "Lab 4", c: "#4F8F66" }, null, null
  ]},
  { t: "3:00 – 4:00", row: [
    { s: "Physical Education", r: "Ground", c: "#7A1E1E" },
    { s: "Mathematics", r: "Room 201", c: "#2E5A91" },
    { s: "Physical Education", r: "Ground", c: "#7A1E1E" },
    { s: "Biology", r: "Lab 4", c: "#4F8F66" },
    { s: "Tutorial", r: "Room 101", c: "#8c7748" }, null, null
  ]},
];

const TT_SUBJECTS = [
  { name: "Mathematics", count: 5, color: "#2E5A91" },
  { name: "Physics", count: 4, color: "#6A4F8F" },
  { name: "Chemistry", count: 4, color: "#3D7A4D" },
  { name: "English", count: 4, color: "#B58A36" },
  { name: "History", count: 4, color: "#7A1E1E" },
  { name: "Computer Science", count: 3, color: "#2E5A91" },
  { name: "Biology", count: 3, color: "#4F8F66" },
  { name: "Physical Education", count: 2, color: "#7A1E1E" },
  { name: "Tutorial", count: 1, color: "#8c7748" },
];

const SEED_TASKS = [
  { id: 1, name: "Complete Maths Worksheet 12", sub: "Mathematics", time: "5:00 PM", done: false, star: true },
  { id: 2, name: "Read Chapter 3 — Cell Structure", sub: "Biology", time: "6:30 PM", done: false },
  { id: 3, name: "English Essay Draft", sub: "English", time: "7:30 PM", done: false },
  { id: 4, name: "Revise History Notes", sub: "History", time: "8:30 PM", done: false },
  { id: 5, name: "Practice Physics Numericals", sub: "Physics", time: "9:30 PM", done: false },
];
const UPCOMING = [
  { date: "May 15", title: "Chemistry Lab Report", time: "10:00 AM" },
  { date: "May 15", title: "French Vocabulary Test", time: "2:00 PM" },
  { date: "May 16", title: "Computer Science Project", time: "11:00 AM" },
];

const COLLECTIONS = [
  { name: "All Items", count: 48 },
  { name: "School Books", count: 16 },
  { name: "Class Notes", count: 12 },
  { name: "Reference Books", count: 8 },
  { name: "Past Papers", count: 6 },
  { name: "Personal", count: 6 },
];
const LIB_BOOKS = [
  { name: "Class 12 Physics Textbook", meta: "PDF • 12.4 MB", date: "Added 2 days ago" },
  { name: "Organic Chemistry Notes", meta: "PDF • 3.8 MB", date: "Added 3 days ago" },
  { name: "A Brief History of Time", meta: "PDF • 5.2 MB", date: "Added 1 week ago" },
  { name: "Hamlet — Key Themes", meta: "PDF • 1.1 MB", date: "Added 1 week ago" },
  { name: "Computer Science Algorithms", meta: "PDF • 2.7 MB", date: "Added 2 weeks ago" },
  { name: "The Republic (Plato)", meta: "PDF • 4.1 MB", date: "Added 2 weeks ago" },
];

const AMBIENCES = [
  { id: "library", label: "Library", icon: I.book },
  { id: "rain", label: "Rain", icon: I.rain },
  { id: "cafe", label: "Café", icon: I.cafe },
  { id: "woods", label: "Woods", icon: I.woods },
];

const SUBJECT_DOTS = ["#7A1E1E", "#2E5A91", "#8c7748", "#3D7A4D", "#6A4F8F", "#B58A36", "#C9A24A"];

const SET_PREFS = [
  { id: "general", label: "General", icon: I.settings },
  { id: "appearance", label: "Appearance", icon: I.settings },
  { id: "notifications", label: "Notifications", icon: I.bell },
  { id: "privacy", label: "Privacy", icon: I.settings },
  { id: "reminders", label: "Study Reminders", icon: I.bell },
  { id: "backup", label: "Backup & Sync", icon: I.settings },
];
const SET_ACCT = [
  { id: "profile", label: "Profile" },
  { id: "sub", label: "Subscription" },
  { id: "sec", label: "Security" },
];

/* ============================== APP ============================== */
const fmtTime = (s) => {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export default function App() {
  const [view, setView] = useState("home");
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState({ show: false, msg: "" });

  // Tasks (used in home + checklist)
  const [homeTasks, setHomeTasks] = useState(TASKS_HOME);
  const [tasks, setTasks] = useState(SEED_TASKS);
  const [clTab, setClTab] = useState("today");

  // Notes
  const [activeFolder, setActiveFolder] = useState("all");
  const [activeNote, setActiveNote] = useState(1);

  // Library
  const [libTab, setLibTab] = useState("books");
  const [activeColl, setActiveColl] = useState("All Items");

  // Timetable
  const [ttTab, setTtTab] = useState("weekly");

  // Focus
  const [focusMin, setFocusMin] = useState(45);
  const [secLeft, setSecLeft] = useState(45 * 60);
  const [running, setRunning] = useState(false);
  const [amb, setAmb] = useState("library");
  const [volume, setVolume] = useState(60);
  const [strict, setStrict] = useState(false);

  // Settings
  const [setTab, setSetTab] = useState("general");
  const [setColor, setSetColor] = useState(0);
  const [autoBackup, setAutoBackup] = useState(true);
  const [syncDevices, setSyncDevices] = useState(true);

  const tickRef = useRef(null);
  const toastRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    tickRef.current = setInterval(() => {
      setSecLeft((s) => {
        if (s <= 1) {
          setRunning(false);
          showToast("Focus session complete.");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast({ show: false, msg: "" }), 2500);
  };

  const toggleHomeTask = (id) =>
    setHomeTasks((arr) => arr.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
  const toggleTask = (id) =>
    setTasks((arr) => arr.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));

  const completed = tasks.filter((t) => t.done).length;
  const pending = tasks.length - completed;
  const pct = tasks.length ? Math.round((completed / tasks.length) * 100) : 0;

  /* ===== Page title per view ===== */
  const Header = () => {
    if (view === "home") {
      return (
        <div className="greeting-block">
          <div className="greeting-hello">Good Evening,</div>
          <div className="greeting-name">Steve.</div>
          <div style={{ color: "var(--gold)", marginTop: 6 }}><Ornament /></div>
        </div>
      );
    }
    const titles = { notes: "Notes", timetable: "Timetable", checklist: "Checklist", library: "Library", focus: "Focus Mode", settings: "Settings" };
    return (
      <div className="page-title">
        <span>{titles[view] || ""}</span>
        <span className="orn"><FleurRule /></span>
      </div>
    );
  };

  return (
    <div className="ledger-app">
      <style>{STYLES}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Lora:ital,wght@0,400;0,500;0,600;1,400&display=swap" />

      {/* SIDEBAR */}
      <aside className="sidebar" data-testid="sidebar">
        <div className="brand">
          <div className="brand-name">LEDGER</div>
          <div className="ornament"><Ornament color="var(--gold)" /></div>
        </div>
        <ul className="nav-list">
          {NAV.map((n) => (
            <li key={n.id}>
              <button
                className={`nav-item ${view === n.id ? "active" : ""}`}
                onClick={() => setView(n.id)}
                data-testid={`nav-${n.id}`}
              >
                {n.icon}<span>{n.label}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="sidebar-quote">
          “Discipline is the bridge between goals and accomplishment.”
          <span className="attrib">— Jim Rohn</span>
          <div className="ornament" style={{ marginTop: 10 }}><Ornament color="var(--gold)" /></div>
        </div>
      </aside>

      {/* MAIN */}
      <main className="app-main">
        <div className="topbar">
          <Header />
          <div className="search-pill">
            {I.search}
            <input
              type="text"
              placeholder="Search notes, tasks, books…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="global-search-input"
            />
          </div>
          <div className="topbar-actions">
            <button className="icon-btn" onClick={() => showToast("You're all caught up.")} data-testid="notifications-btn">
              {I.bell}
            </button>
            <div className="avatar-pill" data-testid="user-pill">
              <div className="avatar">S</div>
              {I.caret}
            </div>
          </div>
        </div>

        <div className="scrollway">
          {view === "home"     && <HomeView homeTasks={homeTasks} toggleHomeTask={toggleHomeTask} setView={setView} />}
          {view === "notes"    && <NotesView activeFolder={activeFolder} setActiveFolder={setActiveFolder} activeNote={activeNote} setActiveNote={setActiveNote} />}
          {view === "timetable"&& <TimetableView ttTab={ttTab} setTtTab={setTtTab} />}
          {view === "checklist"&& <ChecklistView tasks={tasks} toggleTask={toggleTask} clTab={clTab} setClTab={setClTab} completed={completed} pending={pending} pct={pct} />}
          {view === "library"  && <LibraryView libTab={libTab} setLibTab={setLibTab} activeColl={activeColl} setActiveColl={setActiveColl} />}
          {view === "focus"    && <FocusView focusMin={focusMin} setFocusMin={setFocusMin} secLeft={secLeft} setSecLeft={setSecLeft} running={running} setRunning={setRunning} amb={amb} setAmb={setAmb} volume={volume} setVolume={setVolume} strict={strict} setStrict={setStrict} showToast={showToast} />}
          {view === "settings" && <SettingsView setTab={setTab} setSetTab={setSetTab} setColor={setColor} setSetColor={setSetColor} autoBackup={autoBackup} setAutoBackup={setAutoBackup} syncDevices={syncDevices} setSyncDevices={setSyncDevices} />}
        </div>
      </main>

      <div className={`toast ${toast.show ? "show" : ""}`} data-testid="toast">{toast.msg}</div>
    </div>
  );
}

/* ============================= HOME ============================= */
function HomeView({ homeTasks, toggleHomeTask, setView }) {
  return (
    <div className="view" data-testid="view-home">
      <div className="home-grid">
        {/* Continue studying */}
        <div className="card with-corners continue-card" data-testid="continue-card">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="engraving"><Engraving /></div>
          <div className="continue-right">
            <div className="continue-title">Continue Studying</div>
            <div className="continue-sub">You were last working on</div>
            <div className="note-pill">
              <span className="doc-icon">{I.doc}</span>
              <div>
                <div className="note-pill-title">French Revolution Notes</div>
                <div className="note-pill-meta">History • 12 pages</div>
              </div>
            </div>
            <button className="btn btn-primary btn-arrow" onClick={() => setView("notes")} data-testid="open-note-btn">
              Open Note {I.arrow}
            </button>
          </div>
        </div>

        {/* Today's tasks */}
        <div className="card with-corners" data-testid="todays-tasks-card">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="section-title">Today's Tasks</div>
          <div className="section-rule" />
          <div className="tasks-list">
            {homeTasks.map((t) => (
              <div className="task-row" key={t.id} data-testid={`home-task-${t.id}`}>
                <div
                  className={`checkbox ${t.done ? "checked" : ""}`}
                  onClick={() => toggleHomeTask(t.id)}
                  data-testid={`home-task-toggle-${t.id}`}
                />
                <div className="task-name">{t.name}</div>
                <div className="task-sub">— {t.sub}</div>
                <div className="task-time">{t.time}</div>
              </div>
            ))}
          </div>
          <div className="mt-16">
            <span className="link-action" onClick={() => setView("checklist")} data-testid="view-all-tasks">View all tasks →</span>
          </div>
        </div>
      </div>

      <div className="lower-grid">
        {/* Recent notes */}
        <div className="card with-corners" data-testid="recent-notes-card">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="section-title">Recent Notes</div>
          <div className="section-rule" />
          {RECENT_NOTES.map((n) => (
            <div className="doc-row" key={n.id}>
              <div className="doc-icon-box">{I.doc}</div>
              <div className="doc-meta">
                <div className="doc-title">{n.title}</div>
                <div className="doc-sub">{n.sub}</div>
              </div>
              {n.pinned && <span className="pin">{I.pin}</span>}
            </div>
          ))}
          <div className="mt-16"><span className="link-action" onClick={() => setView("notes")}>View all notes →</span></div>
        </div>

        {/* Library recent */}
        <div className="card with-corners" data-testid="library-recent-card">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="section-title">Recently Added in Library</div>
          <div className="section-rule" />
          {LIBRARY_RECENT.map((d) => (
            <div className="doc-row" key={d.id}>
              <div className={`doc-icon-box ${d.type}`}>
                {d.type === "pdf" && I.pdf}
                {d.type === "image" && I.image}
                {d.type === "link" && I.link}
              </div>
              <div className="doc-meta">
                <div className="doc-title">{d.title}</div>
                <div className="doc-sub">{d.sub}</div>
              </div>
            </div>
          ))}
          <div className="mt-16"><span className="link-action" onClick={() => setView("library")}>View full library →</span></div>
        </div>

        {/* Timetable preview */}
        <div className="card with-corners" data-testid="timetable-preview-card">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="row between" style={{ marginBottom: 8 }}>
            <div className="section-title">Timetable Preview</div>
            <span className="link-action" onClick={() => setView("timetable")}>View full timetable →</span>
          </div>
          <div className="section-rule" />
          <table className="mini-tt">
            <thead><tr><th></th>{TT_DAYS.map((d) => <th key={d}>{d}</th>)}</tr></thead>
            <tbody>
              {TT_PREVIEW.map((r, i) => (
                <tr key={i}>
                  <td className="time-col"><strong>{i + 1}</strong><br/>{r.t}</td>
                  {r.row.map((s, j) => (
                    <td key={j}>
                      {s === "—" ? <span className="muted">—</span> :
                        <span className={`subject ${SUBJECT_COLORS[s] || ""}`}>{s.split("\n").map((l, k) => <span key={k} style={{ display: "block" }}>{l}</span>)}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="muted" style={{ fontStyle: "italic", marginTop: 12, fontSize: 13 }}>Saturday is free for self-study and catch-up.</div>
        </div>
      </div>

      {/* Focus banner */}
      <div className="card with-corners focus-banner" data-testid="focus-banner">
        <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
        <LampBookSVG />
        <div>
          <div className="fb-title">Need uninterrupted time?</div>
          <div className="fb-sub">Enter Focus Mode and get things done.</div>
        </div>
        <button className="btn btn-primary" onClick={() => setView("focus")} data-testid="start-focus-banner-btn">
          Start Focus Mode {I.focus}
        </button>
      </div>
    </div>
  );
}

/* ============================= NOTES ============================= */
const FOLDERS = [
  { id: "all", label: "All Notes", count: 24 },
  { id: "history", label: "History", count: 8 },
  { id: "maths", label: "Maths", count: 12 },
  { id: "science", label: "Science", count: 10 },
  { id: "english", label: "English", count: 7 },
  { id: "personal", label: "Personal", count: 3 },
];
function NotesView({ activeFolder, setActiveFolder, activeNote, setActiveNote }) {
  return (
    <div className="view" data-testid="view-notes">
      <div className="notes-layout">
        <div className="card" style={{ padding: 16 }}>
          <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} data-testid="new-note-btn">{I.plus} New Note</button>
          <div className="folder-section-title"><span>FOLDERS</span><span style={{ cursor: "pointer", color: "var(--gold)" }}>+</span></div>
          {FOLDERS.map((f) => (
            <button
              key={f.id}
              className={`folder-item ${activeFolder === f.id ? "active" : ""}`}
              onClick={() => setActiveFolder(f.id)}
              data-testid={`folder-${f.id}`}
            >
              {I.folder}<span>{f.label}</span><span className="count">{f.count}</span>
            </button>
          ))}
          <div className="add-folder">+ New Folder</div>

          <div className="folder-section-title"><span>RECENT NOTES</span></div>
          {RECENT_NOTES.map((n) => (
            <button
              key={n.id}
              className={`folder-item ${activeNote === n.id ? "active" : ""}`}
              onClick={() => setActiveNote(n.id)}
              data-testid={`recent-note-${n.id}`}
              style={{ alignItems: "flex-start" }}
            >
              {I.doc}
              <div style={{ flexGrow: 1 }}>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 600 }}>{n.title}</div>
                <div className="doc-sub">{n.sub}</div>
              </div>
              {n.pinned && <span className="pin">{I.pin}</span>}
            </button>
          ))}
        </div>

        <div className="card editor-panel">
          <div className="editor-head">
            <input className="editor-title-input" defaultValue="French Revolution Notes" data-testid="note-title-input" />
            <span style={{ color: "var(--gold)" }}>{I.pin}</span>
          </div>
          <div className="editor-meta">
            {I.folder}<span>History</span><span>•</span><span>Last edited: Today, 4:30 PM</span>
          </div>
          <div className="editor-toolbar" data-testid="editor-toolbar">
            <button className="tb-btn">H1</button>
            <div className="tb-divider" />
            <button className="tb-btn" style={{ fontWeight: 700 }}>B</button>
            <button className="tb-btn" style={{ fontStyle: "italic" }}>I</button>
            <button className="tb-btn" style={{ textDecoration: "underline" }}>U</button>
            <div className="tb-divider" />
            <button className="tb-btn">•≡</button>
            <button className="tb-btn">1≡</button>
            <button className="tb-btn">≡</button>
            <button className="tb-btn">≡</button>
            <div className="tb-divider" />
            <button className="tb-btn">{I.link}</button>
            <button className="tb-btn">{I.image}</button>
            <button className="tb-btn">⊞</button>
            <div className="tb-divider" />
            <button className="tb-btn">↶</button>
            <button className="tb-btn">↷</button>
          </div>

          <div className="note-body" data-testid="note-body">
            <h3>1. Causes of the French Revolution</h3>
            <ul>
              <li><strong>Social Inequality:</strong> The society was divided into three estates. The Third Estate, which included the common people, had to bear the heavy burden of taxes.</li>
              <li><strong>Economic Crisis:</strong> France was in deep debt due to expensive wars and royal spending.</li>
              <li><strong>Political Inefficiency:</strong> The monarchy had absolute power with little input from the people.</li>
              <li><strong>Enlightenment Ideas:</strong> Ideas of liberty, equality and fraternity inspired people to fight for their rights.</li>
            </ul>
            <div className="fleur-rule"><span style={{ color: "var(--gold)" }}>❦</span></div>
            <h3>2. Key Events Timeline</h3>
            <table className="timeline">
              <thead><tr><th>Year</th><th>Event</th></tr></thead>
              <tbody>
                <tr><td>1789</td><td>Estates-General convened in May</td></tr>
                <tr><td>1789</td><td>Storming of the Bastille on 14th July</td></tr>
                <tr><td>1789</td><td>Declaration of the Rights of Man and Citizen</td></tr>
                <tr><td>1792</td><td>Monarchy abolished; France becomes a Republic</td></tr>
                <tr><td>1793–1794</td><td>Reign of Terror</td></tr>
              </tbody>
            </table>
            <div className="fleur-rule"><span style={{ color: "var(--gold)" }}>❦</span></div>
            <h3>3. Important Notes</h3>
            <div className="callout">
              <Quill />
              <div>The French Revolution was not just a political revolution, but a social and cultural upheaval that changed the course of European history.</div>
            </div>
          </div>
          <div className="row between" style={{ marginTop: 24, color: "var(--muted)", fontSize: 13 }}>
            <span>812 words</span>
            <span>Saved ✓ &nbsp;&nbsp; Auto-saved at 4:30 PM</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================= TIMETABLE ============================= */
function TimetableView({ ttTab, setTtTab }) {
  return (
    <div className="view" data-testid="view-timetable">
      <div className="tt-bar">
        <div className="tt-tabs">
          <button className={`tt-tab ${ttTab === "weekly" ? "active" : ""}`} onClick={() => setTtTab("weekly")} data-testid="tt-weekly">{I.calendar} Weekly View</button>
          <button className={`tt-tab ${ttTab === "calendar" ? "active" : ""}`} onClick={() => setTtTab("calendar")} data-testid="tt-calendar">{I.calendar} Calendar View</button>
        </div>
        <div className="tt-actions">
          <button className="btn btn-ghost">{I.settings} Manage Timetable</button>
          <button className="btn btn-primary" data-testid="add-class-btn">{I.plus} Add Class</button>
        </div>
      </div>

      <div className="row" style={{ gap: 12, padding: "8px 0 20px" }}>
        <button className="icon-btn">{I.chev_l}</button>
        <button className="icon-btn">{I.calendar}</button>
        <button className="icon-btn">{I.chev_r}</button>
        <span style={{ fontFamily: "var(--font-display)", fontSize: 22, marginLeft: 12 }}>May 20 – May 26, 2024</span>
      </div>

      <div className="tt-grid">
        <div className="card tt-table-wrap">
          <table className="tt-table">
            <thead>
              <tr>
                <th></th>
                {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => <th key={d}>{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {FULL_TT.map((r, i) => (
                <tr key={i}>
                  <td className="time-cell">{r.t.split(" – ").map((p, k) => <div key={k}>{p}{k === 0 ? <div>–</div> : ""}</div>)}</td>
                  {r.row.map((cell, j) => (
                    <td key={j} className="tt-cell">
                      {!cell ? <div className="tt-block empty">—</div> :
                       r.lunch ? <div className="tt-block" style={{ background: "#EDE1BD", textAlign: "center" }}>{cell}</div> :
                       <div className="tt-block" style={{ background: cell.c + "1F", borderLeftColor: cell.c }}>
                         <span className="name">{cell.s}</span>
                         <span className="room">{cell.r}</span>
                       </div>
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="card">
            <div className="row between" style={{ marginBottom: 6 }}>
              <span style={{ fontFamily: "var(--font-display)", fontSize: 22 }}>Subjects</span>
              <span className="muted">⋯</span>
            </div>
            <div className="section-rule" />
            <div className="subject-card">
              {TT_SUBJECTS.map((s) => (
                <div className="subj-row" key={s.name}>
                  <span className="subj-dot" style={{ background: s.color }} />
                  <span className="subj-name">{s.name}</span>
                  <span className="subj-count">{s.count}</span>
                </div>
              ))}
              <div className="subj-total"><span>Total Classes</span><span>30</span></div>
            </div>
          </div>

          <div className="card">
            <span style={{ fontFamily: "var(--font-display)", fontSize: 22 }}>Quick Actions</span>
            <div className="section-rule" />
            <div className="qa-row">{I.plus} Add Class</div>
            <div className="qa-row">{I.cafe} Add Break</div>
            <div className="qa-row">{I.book} Add Study Block</div>
            <div className="qa-row">{I.arrow} Import Timetable</div>
            <div className="qa-row">{I.doc} Print Timetable</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================= CHECKLIST ============================= */
function ChecklistView({ tasks, toggleTask, clTab, setClTab, completed, pending, pct }) {
  return (
    <div className="view" data-testid="view-checklist">
      <div className="checklist-bar">
        <div className="tt-tabs">
          {["today", "week", "all"].map((id) => (
            <button key={id} className={`tt-tab ${clTab === id ? "active" : ""}`} onClick={() => setClTab(id)} data-testid={`cl-tab-${id}`}>
              {id === "today" ? "TODAY" : id === "week" ? "THIS WEEK" : "ALL TASKS"}
            </button>
          ))}
        </div>
        <div className="tt-actions">
          <button className="btn btn-primary" onClick={() => {}} data-testid="add-task-btn">{I.plus} Add Task</button>
        </div>
      </div>

      <div className="cl-grid">
        <div className="card with-corners">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="row between">
            <div className="section-title">Today's Tasks</div>
            <span className="muted">May 14, 2024</span>
          </div>
          <div className="section-rule" />
          {tasks.map((t) => (
            <div className={`cl-task ${t.done ? "done" : ""}`} key={t.id} data-testid={`task-${t.id}`}>
              <div className={`checkbox ${t.done ? "checked" : ""}`} onClick={() => toggleTask(t.id)} data-testid={`task-toggle-${t.id}`} />
              <div className="grow">
                <div className="task-name">{t.name}</div>
                <div className="muted" style={{ fontSize: 13 }}>{t.sub}</div>
              </div>
              {t.star && <span className="star">★</span>}
              <span className="muted">{t.time}</span>
            </div>
          ))}
          <div style={{ color: "var(--crimson)", padding: 14, cursor: "pointer", fontFamily: "var(--font-display)", fontSize: 16 }}>+ Add Task</div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="card with-corners">
            <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
            <div className="section-title">Progress</div>
            <div className="section-rule" />
            <div className="donut-wrap">
              <svg viewBox="0 0 100 100" className="donut">
                <circle cx="50" cy="50" r="42" fill="none" strokeWidth="10" className="ring-bg" />
                <circle cx="50" cy="50" r="42" fill="none" strokeWidth="10" className="ring-fg"
                  strokeDasharray={2 * Math.PI * 42}
                  strokeDashoffset={(1 - pct / 100) * 2 * Math.PI * 42}
                  transform="rotate(-90 50 50)" strokeLinecap="round"
                />
                <text x="50" y="50" className="donut-text" dy="0.35em">{pct}%</text>
                <text x="50" y="68" className="donut-sub">COMPLETED</text>
              </svg>
              <div>
                <div className="kpi-row">
                  <div className="kpi"><div className="num">{completed}</div><div className="lbl">Completed</div></div>
                  <div className="kpi"><div className="num">{pending}</div><div className="lbl">Pending</div></div>
                </div>
                <div className="divider-soft" />
                <div className="kpi" style={{ textAlign: "left" }}>
                  <div className="num">{tasks.length}</div><div className="lbl">Total Tasks</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card with-corners">
            <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
            <div className="section-title">Upcoming</div>
            <div className="section-rule" />
            {UPCOMING.map((u, i) => (
              <div className="cl-task" key={i}>
                <div className="grow row gap-12">
                  <span className="muted" style={{ width: 60 }}>{u.date}</span>
                  <span className="task-name" style={{ fontSize: 16 }}>{u.title}</span>
                </div>
                <span className="muted">{u.time}</span>
              </div>
            ))}
            <div style={{ color: "#1f4f96", padding: 14, cursor: "pointer", fontSize: 14 }}>View all tasks →</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================= LIBRARY ============================= */
function LibraryView({ libTab, setLibTab, activeColl, setActiveColl }) {
  return (
    <div className="view" data-testid="view-library">
      <div className="lib-tabs">
        {[
          { id: "books", label: "BOOKS & NOTES" },
          { id: "pdfs", label: "PDFS" },
          { id: "links", label: "LINKS" },
          { id: "images", label: "IMAGES" },
        ].map((t) => (
          <button key={t.id} className={`tt-tab ${libTab === t.id ? "active" : ""}`} onClick={() => setLibTab(t.id)} data-testid={`lib-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
        <div className="tt-actions">
          <button className="btn btn-primary" data-testid="add-new-lib">{I.plus} Add New</button>
          <button className="btn btn-ghost">Sort by: Recent {I.caret}</button>
        </div>
      </div>

      <div className="lib-grid">
        <div className="card">
          <div className="section-title row between"><span>COLLECTIONS</span><span style={{ color: "var(--gold)", cursor: "pointer" }}>+</span></div>
          <div className="section-rule" />
          {COLLECTIONS.map((c) => (
            <div className={`coll-row ${activeColl === c.name ? "active" : ""}`} key={c.name} onClick={() => setActiveColl(c.name)} data-testid={`coll-${c.name.replace(/\s+/g, '-').toLowerCase()}`}>
              {I.folder}<span>{c.name}</span><span className="count">{c.count}</span>
            </div>
          ))}
          <div className="divider-soft" />
          <div className="section-title">Storage</div>
          <div className="storage-bar"><div className="storage-fill" /></div>
          <div className="muted" style={{ fontSize: 13 }}>2.34 GB of 10 GB used</div>
          <button className="btn btn-ghost" style={{ width: "100%", justifyContent: "center", marginTop: 14 }}>Upgrade Storage</button>
        </div>

        <div className="card">
          <div className="book-grid">
            {LIB_BOOKS.map((b, i) => (
              <div className="book-card" key={i} data-testid={`book-${i}`}>
                <div className="book-thumb pdf">
                  <svg viewBox="0 0 80 100" width="80" height="100" fill="currentColor" fillOpacity="0.85">
                    <path d="M10 0h50l20 20v80H10z" fillOpacity="0.85"/>
                    <path d="M60 0v20h20" fill="#fff" fillOpacity="0.2"/>
                  </svg>
                </div>
                <div className="book-name">{b.name}</div>
                <div className="book-meta">{b.meta}</div>
                <div className="book-date">{b.date}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================= FOCUS ============================= */
function FocusView({ focusMin, setFocusMin, secLeft, setSecLeft, running, setRunning, amb, setAmb, volume, setVolume, strict, setStrict, showToast }) {
  const start = () => { if (secLeft <= 0) setSecLeft(focusMin * 60); setRunning(true); showToast("Focus session started."); };
  const stop = () => setRunning(false);
  return (
    <div className="view" data-testid="view-focus">
      <div className="row between" style={{ marginBottom: 16 }}>
        <span style={{ color: "var(--gold)" }}><Ornament color="var(--gold)" /></span>
        <button className="btn btn-ghost">Focus History</button>
      </div>

      <div className="focus-grid">
        <div className="card with-corners">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="row" style={{ gap: 32, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <div className="timer-label">Session</div>
              <div className="focus-timer-display" data-testid="focus-timer">{fmtTime(secLeft)}</div>
              <div className="muted" style={{ textAlign: "center", fontSize: 13 }}>minutes</div>
              <div style={{ marginTop: 18, textAlign: "center" }}>
                <select className="select-pill" value={focusMin} onChange={(e) => { setFocusMin(+e.target.value); setSecLeft(+e.target.value * 60); }} data-testid="focus-duration">
                  {[15, 25, 45, 60, 90].map((m) => <option key={m} value={m}>Break after {m} minutes ▾</option>)}
                </select>
              </div>
              <div style={{ marginTop: 20, textAlign: "center" }}>
                {!running ? (
                  <button className="btn btn-primary" onClick={start} data-testid="focus-start-btn" style={{ padding: "12px 28px" }}>Start Focus Session</button>
                ) : (
                  <button className="btn btn-primary" onClick={stop} data-testid="focus-pause-btn" style={{ padding: "12px 28px" }}>Pause Session</button>
                )}
              </div>
              <div className="muted" style={{ textAlign: "center", marginTop: 12, fontStyle: "italic", fontSize: 13 }}>
                No music. No distractions.<br/>Just you and your goals.
              </div>
            </div>
            <div style={{ width: 200, color: "#8a6a2a" }}>
              <LampBookSVG />
            </div>
          </div>
        </div>

        <div className="card with-corners">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="section-title">Ambience</div>
          <div className="section-rule" />
          <div className="ambience-grid">
            {AMBIENCES.map((a) => (
              <button
                key={a.id}
                className={`amb-btn ${amb === a.id ? "active" : ""}`}
                onClick={() => setAmb(a.id)}
                data-testid={`amb-${a.id}`}
              >
                {a.icon}
                <span>{a.label}</span>
              </button>
            ))}
          </div>

          <div className="section-title mt-24">Sound</div>
          <div className="section-rule" />
          <div className="sound-row">
            <span>🔈</span>
            <input type="range" min="0" max="100" value={volume} onChange={(e) => setVolume(+e.target.value)} data-testid="focus-volume" />
            <span>🔊</span>
          </div>

          <div className="toggle-row" style={{ marginTop: 18 }}>
            <div className={`checkbox ${strict ? "checked" : ""}`} onClick={() => setStrict(!strict)} data-testid="focus-strict" />
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>Enable strict mode</div>
              <div className="muted" style={{ fontSize: 12 }}>Block distracting websites and notifications.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="focus-stats-grid">
        <div className="card with-corners">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="section-title">Today's Focus Goal</div>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 32, marginTop: 8 }}>2 / 3 <span style={{ fontSize: 18, color: "var(--muted)" }}>hours</span></div>
          <div className="progress-bar" style={{ marginTop: 8 }}><div className="progress-fill" /></div>
          <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>Keep going, you're doing great.</div>
        </div>
        <div className="card with-corners">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="section-title">Total Focus Time</div>
          <div className="row" style={{ gap: 32, marginTop: 8 }}>
            <div><div style={{ fontFamily: "var(--font-display)", fontSize: 28 }}>6h 30m</div><div className="muted" style={{ fontSize: 12 }}>This week</div></div>
            <div><div style={{ fontFamily: "var(--font-display)", fontSize: 28 }}>12h 45m</div><div className="muted" style={{ fontSize: 12 }}>This month</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================= SETTINGS ============================= */
function SettingsView({ setTab, setSetTab, setColor, setSetColor, autoBackup, setAutoBackup, syncDevices, setSyncDevices }) {
  return (
    <div className="view" data-testid="view-settings">
      <div className="set-grid">
        <div className="card" style={{ padding: 16 }}>
          <div className="set-section-title">Preferences</div>
          {SET_PREFS.map((p) => (
            <div key={p.id} className={`set-row ${setTab === p.id ? "active" : ""}`} onClick={() => setSetTab(p.id)} data-testid={`set-pref-${p.id}`}>
              {p.icon}<span>{p.label}</span>
            </div>
          ))}
          <div className="set-section-title">Account</div>
          {SET_ACCT.map((p) => (
            <div key={p.id} className="set-row" data-testid={`set-acct-${p.id}`}>
              {I.settings}<span>{p.label}</span>
            </div>
          ))}
        </div>

        <div className="card with-corners">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div style={{ fontFamily: "var(--font-display)", fontSize: 26, marginBottom: 6 }}>General Settings</div>
          <div className="section-rule" />
          <div className="field-row">
            <div className="field-label">Language</div>
            <select className="select-pill"><option>English</option></select>
          </div>
          <div className="field-row">
            <div className="field-label">Time Zone</div>
            <select className="select-pill"><option>(GMT+05:30) Asia/Kolkata</option></select>
          </div>
          <div className="field-row">
            <div className="field-label">Start week on</div>
            <select className="select-pill"><option>Monday</option></select>
          </div>
          <div className="field-row">
            <div className="field-label">Default view for timetable</div>
            <select className="select-pill"><option>Weekly View</option></select>
          </div>
          <div className="field-row">
            <div className="field-label">Default subject color</div>
            <div className="color-dots">
              {SUBJECT_DOTS.map((c, i) => (
                <span key={i} className={`color-dot ${setColor === i ? "active" : ""}`} style={{ background: c }} onClick={() => setSetColor(i)} data-testid={`color-${i}`} />
              ))}
            </div>
          </div>

          <div style={{ fontFamily: "var(--font-display)", fontSize: 26, marginTop: 28, marginBottom: 6 }}>Data & Sync</div>
          <div className="section-rule" />
          <div className="field-row">
            <div>
              <div className="field-label">Auto backup</div>
              <div className="field-sub">Automatically back up your data daily.</div>
            </div>
            <div className={`switch ${autoBackup ? "on" : ""}`} onClick={() => setAutoBackup(!autoBackup)} data-testid="toggle-autobackup" />
          </div>
          <div className="field-row">
            <div>
              <div className="field-label">Sync across devices</div>
              <div className="field-sub">Keep your notes, tasks and timetable in sync.</div>
            </div>
            <div className={`switch ${syncDevices ? "on" : ""}`} onClick={() => setSyncDevices(!syncDevices)} data-testid="toggle-sync" />
          </div>

          <button className="btn btn-primary" style={{ marginTop: 24 }} data-testid="save-settings">Save Changes</button>
        </div>

        <div className="card with-corners">
          <span className="corner-tl" /><span className="corner-tr" /><span className="corner-bl" /><span className="corner-br" />
          <div className="section-title">About</div>
          <div className="section-rule" />
          <div style={{ fontFamily: "var(--font-display)", fontSize: 20 }}>Ledger Study Planner</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>Version 1.0.0</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 12 }}>© 2026 Ledger. All rights reserved.</div>

          <div style={{ textAlign: "center", marginTop: 28, color: "#8a6a2a" }}>
            <svg viewBox="0 0 100 80" width="120" height="100">
              <rect x="20" y="50" width="60" height="8" fill="currentColor" opacity="0.35"/>
              <rect x="22" y="40" width="56" height="10" fill="currentColor" opacity="0.5"/>
              <rect x="24" y="30" width="52" height="10" fill="currentColor" opacity="0.65"/>
              <rect x="26" y="20" width="48" height="10" fill="currentColor" opacity="0.8"/>
            </svg>
          </div>

          <div style={{ marginTop: 22, textAlign: "center", fontStyle: "italic", color: "var(--ink-soft)", fontFamily: "var(--font-display)", fontSize: 17, lineHeight: 1.5 }}>
            "Discipline is the bridge between goals<br/>and accomplishment."
            <div style={{ marginTop: 8, color: "var(--muted)", fontStyle: "normal" }}>— Jim Rohn</div>
          </div>
        </div>
      </div>
    </div>
  );
}
